export default class UploadQueueManager {
  constructor() {
    this.activeUploads = new Map();
    this.retryQueue = new Map();
    this.maxRetries = 5;
    this.retryDelays = [1000, 2000, 4000, 8000, 16000];
    this.eventListeners = new Map();
  }

  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  emit(event, data) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  isUploadActive(tag) {
    return this.activeUploads.has(tag);
  }

  setUploadActive(tag, uploadInfo) {
    this.activeUploads.set(tag, {
      ...uploadInfo,
      startTime: Date.now(),
      status: 'uploading'
    });
  }

  removeActiveUpload(tag) {
    this.activeUploads.delete(tag);
  }

  addToRetryQueue(tag, uploadData, error) {
    const existingRetry = this.retryQueue.get(tag);
    const retryCount = existingRetry ? existingRetry.retryCount + 1 : 1;

    if (retryCount > this.maxRetries) {
      console.error(`📸 Upload máximo de tentativas atingido para ${tag}:`, error);
      
      this.emit('maxRetriesReached', {
        tag,
        uploadData,
        error,
        retryCount
      });
      
      this.retryQueue.delete(tag);
      this.removeActiveUpload(tag);
      return false;
    }

    const retryInfo = {
      ...uploadData,
      retryCount,
      lastError: error,
      nextRetryTime: Date.now() + this.retryDelays[retryCount - 1],
      status: 'queued_for_retry'
    };

    this.retryQueue.set(tag, retryInfo);
    this.removeActiveUpload(tag);

    this.emit('retry', {
      tag,
      retryCount,
      maxRetries: this.maxRetries,
      nextRetryTime: retryInfo.nextRetryTime
    });

    setTimeout(() => {
      this.processRetry(tag);
    }, this.retryDelays[retryCount - 1]);

    return true;
  }

  async processRetry(tag) {
    const retryInfo = this.retryQueue.get(tag);
    
    if (!retryInfo) {
      console.warn(`📸 Retry info não encontrada para ${tag}`);
      return;
    }

    if (Date.now() < retryInfo.nextRetryTime) {
      console.log(`📸 Ainda não é hora do retry para ${tag}`);
      return;
    }

    this.retryQueue.delete(tag);
    this.setUploadActive(tag, retryInfo);

    try {
      await retryInfo.uploadFunction();
      
      this.removeActiveUpload(tag);
      
      this.emit('success', {
        tag,
        retryCount: retryInfo.retryCount,
        uploadData: retryInfo
      });
      
    } catch (error) {
      console.error(`📸 Retry ${retryInfo.retryCount} falhou para ${tag}:`, error);
      
      const isBlobError = error.message.includes('Backup da mídia não encontrado') || 
                         error.message.includes('InvalidBlob') ||
                         error.message.includes('Failed to write blobs') ||
                         error.name === 'DataError';
      
      this.emit('error', {
        tag,
        error,
        retryCount: retryInfo.retryCount,
        uploadData: retryInfo,
        isBlobError
      });

      if (isBlobError) {
        console.error(`📸 Erro de blob detectado para ${tag}, removendo da fila de retry`);
        this.retryQueue.delete(tag);
        this.removeActiveUpload(tag);
        
        this.emit('maxRetriesReached', {
          tag,
          uploadData: retryInfo,
          error: new Error('Arquivo não está mais disponível. Tente tirar a foto novamente.'),
          retryCount: retryInfo.retryCount
        });
      } else {
        this.addToRetryQueue(tag, retryInfo, error);
      }
    }
  }

  cancelUpload(tag) {
    this.removeActiveUpload(tag);
    this.retryQueue.delete(tag);
  }

  getUploadStatus(tag) {
    if (this.activeUploads.has(tag)) {
      return this.activeUploads.get(tag);
    }
    
    if (this.retryQueue.has(tag)) {
      return this.retryQueue.get(tag);
    }
    
    return null;
  }

  getActiveUploads() {
    return Array.from(this.activeUploads.entries()).map(([tag, info]) => ({
      tag,
      ...info
    }));
  }

  getRetryQueue() {
    return Array.from(this.retryQueue.entries()).map(([tag, info]) => ({
      tag,
      ...info
    }));
  }

  clearAll() {
    this.activeUploads.clear();
    this.retryQueue.clear();
  }

  hasPendingUploads() {
    return this.activeUploads.size > 0 || this.retryQueue.size > 0;
  }

  getStats() {
    return {
      activeUploads: this.activeUploads.size,
      retryQueue: this.retryQueue.size,
      totalPending: this.activeUploads.size + this.retryQueue.size
    };
  }
} 